import React from "react";
import FemalePatient from "../assets/scanner-female.png";
import MalePatient from "../assets/scanner-male.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male.jpg";
import styles from "./GenericFHIRViewerModal.module.css";

const EncounterCardExtended = ({ resource, patientData, providerData, handleShowNested }) => {
  const encounterType = resource.type?.[0]?.text || "Unknown";
  const encounterStatus = resource.status || "Unknown";
  const encounterClass = resource.class?.code || "Unknown";
  const start = resource.period?.start ? new Date(resource.period.start).toLocaleString() : "Unknown";
  const end = resource.period?.end ? new Date(resource.period.end).toLocaleString() : "Unknown";

  const patient = patientData;
  const provider = providerData;

  const patientName = patient?.name?.[0];
  const providerName = provider?.name?.[0];

  const patientFullName = patientName
    ? `${patientName.given?.join(" ")} ${patientName.family}`
    : null;

  const providerFullName = providerName
    ? `${providerName.prefix?.join(" ")} ${providerName.given?.join(" ")} ${providerName.family}`
    : null;

  const patientPhoto =
    patient?.photo?.[0]?.url ||
    (patient?.gender === "female"
      ? FemalePatient
      : patient?.gender === "male"
      ? MalePatient
      : null);

  const providerPhoto =
    provider?.photo?.[0]?.url ||
    (provider?.gender === "female"
      ? FemaleProvider
      : provider?.gender === "male"
      ? MaleProvider
      : null);

  const codingEntries = resource.type?.[0]?.coding || [];

  // Helper: display name + clickable ID fallback
  const renderReference = (ref, name, onClick) => {
    if (!ref) return <span className="text-muted">N/A</span>;

    return (
      <>
        {name ? (
          <>
            {name}{" "}
            <button
              className="btn btn-link p-0"
              onClick={() => onClick?.(ref, name)}
            >
              ({ref})
            </button>
          </>
        ) : (
          <button
            className="btn btn-link p-0"
            onClick={() => onClick?.(ref, ref)}
          >
            {ref}
          </button>
        )}
      </>
    );
  };

  return (
    <div className="p-4 border rounded shadow-sm bg-white">
      {/* Portraits and Vitals */}
      <div className="d-flex justify-content-between mb-4">
        {/* Patient Section */}
        <div className="d-flex align-items-center">
          <button
            onClick={() => handleShowNested?.(patient, patientFullName || "Patient")}
            style={{
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              marginRight: "1rem",
            }}
          >
            <img
              src={patientPhoto}
              alt="Patient Portrait"
              style={{
                width: "100px",
                height: "100px",
                objectFit: "cover",
                borderRadius: "8px",
                border: "1px solid #ccc",
                backgroundColor: "#f8f9fa",
              }}
            />
          </button>
          <div>
            <div className="mb-1 text-muted">
              <strong>Patient:</strong> {patientFullName || "Unknown"}
            </div>
            <div className="mb-1 text-muted">
              <strong>Gender:</strong> {patient?.gender}
            </div>
            <div className="mb-1 text-muted">
              <strong>Birth Date:</strong> {patient?.birthDate || "Unknown"}
            </div>
          </div>
        </div>

        {/* Provider Section */}
        <div className="d-flex align-items-center">
          {provider ? (
            <>
              <button
                onClick={() => handleShowNested?.(provider, providerFullName || "Provider")}
                style={{
                  padding: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  marginRight: "1rem",
                }}
              >
                <img
                  src={providerPhoto}
                  alt="Provider Portrait"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #ccc",
                    backgroundColor: "#f8f9fa",
                  }}
                />
              </button>
              <div>
                <div className="mb-1 text-muted">
                  <strong>Provider:</strong> {providerFullName || "Unknown"}
                </div>
                <div className="mb-1 text-muted">
                  <strong>Gender:</strong> {provider?.gender}
                </div>
                <div className="mb-1 text-muted">
                  <strong>Active:</strong> {provider?.active ? "Yes" : "No"}
                </div>
              </div>
            </>
          ) : (
            <div className="text-muted">
              <strong>No Provider associated with this encounter.</strong>
            </div>
          )}
        </div>
      </div>

      {/* Section: Encounter Details */}
      <div className={styles.sectionHeader}>Encounter Details</div>
      <div className="ps-5 pt-2">
        <div className="mb-1">
          <strong>Encounter ID:</strong> {resource.id}
        </div>
        <div className="mb-1">
          <strong>Status:</strong> {encounterStatus}
        </div>
        <div className="mb-1">
          <strong>Class:</strong> {encounterClass}
        </div>
        <div className="mb-1">
          <strong>Start Time:</strong> {start}
        </div>
        <div className="mb-1">
          <strong>End Time:</strong> {end}
        </div>
      </div>

      {/* Section: Coding System */}
      {codingEntries.length > 0 && (
        <>
          <div className={styles.sectionHeader}>Coding System</div>
          <div className="ps-5 pt-2">
            {codingEntries.map((coding, idx) => (
              <div key={idx} className="mb-2">
                <div>
                  <strong>System:</strong> {coding.system || "Unknown"}
                </div>
                <div>
                  <strong>Code:</strong> {coding.code || "Unknown"}
                </div>
                <div>
                  <strong>Display:</strong> {coding.display || "Unknown"}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Section: References */}
      <div className={styles.sectionHeader}>References</div>
      <div className="ps-5 pt-2">
        <div className="mb-1">
          <strong>Patient Reference:</strong>{" "}
          {renderReference(resource.subject?.reference, patientFullName, handleShowNested)}
        </div>
        <div className="mb-1">
          <strong>Provider Reference:</strong>{" "}
          {provider
            ? renderReference(
                resource.participant?.[0]?.individual?.reference,
                providerFullName,
                handleShowNested
              )
            : <span className="text-muted">No Provider associated with this encounter.</span>}
        </div>
        <div className="mb-1">
          <strong>Service Provider:</strong>{" "}
          {renderReference(resource.serviceProvider?.reference, "", handleShowNested)}
        </div>
      </div>
    </div>
  );
};

export default EncounterCardExtended;
